/**
 * tableColumns.ts — derives content-proportional column widths for a table
 * from the `data-bbox` attributes the PDF→HTML converter (marker) leaves on
 * `<td>`/`<th>` cells. Pure, static, no DOM measurement.
 *
 * `data-bbox` is "x0 y0 x1 y1" in PDF page units; only the x-axis is used. A
 * cell's left edge (x0) and right edge (x1) are votes for the table's vertical
 * grid lines (column boundaries). The median across rows smooths PDF jitter;
 * each column's width is the gap between consecutive boundaries. The output
 * feeds weighted `fr` units in the renderer, so the table still fills 100%
 * width and still wraps via a min-content floor — it only fixes the
 * proportions.
 *
 * ALL-OR-NOTHING: if any column boundary lacks bbox data, or the data is
 * inconsistent (non-monotonic), this returns undefined and the renderer falls
 * back to CSS intrinsic sizing (`minmax(min-content, auto)`).
 */

import { Placement } from "./tableLayout";

interface Bbox {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
}

export function parseBbox(s?: string): Bbox | undefined {
    if (!s) return undefined;
    const p = s.trim().split(/\s+/).map(Number);
    if (p.length !== 4 || p.some((n) => !Number.isFinite(n))) return undefined;
    const [x0, y0, x1, y1] = p;
    return x1 > x0 ? { x0, y0, x1, y1 } : undefined;
}

function median(xs: number[]): number | undefined {
    if (xs.length === 0) return undefined;
    const s = [...xs].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/** px spread within one boundary's votes beyond which we warn (rows disagree). */
const JITTER_TOL = 12;

/**
 * Relative column widths (raw px, used directly as `fr` weights) derived from
 * each placement's `data-bbox` x-edges — or undefined if the table lacks usable
 * bbox data. Placements come from `computeTableLayout` so `col`/`colSpan` are
 * already resolved against `rowspan` carry from earlier rows.
 */
export function deriveColumnFractions(placements: Placement[], colCount: number): number[] | undefined {
    if (colCount < 2) return undefined; // single column → nothing to proportion

    // colCount + 1 vertical grid lines. A cell at column `col` with `colSpan`
    // votes its left edge for boundary[col] and its right edge for
    // boundary[col + colSpan] — colspan needs no special-casing, and internal
    // boundaries inside a span are recovered from non-spanning rows.
    const votes: number[][] = Array.from({ length: colCount + 1 }, () => []);
    let seen = 0;
    for (const p of placements) {
        const bb = parseBbox(p.cell.properties["data-bbox"]);
        if (bb) {
            votes[p.col].push(bb.x0);
            votes[p.col + p.colSpan].push(bb.x1);
            seen++;
        }
    }
    if (seen === 0) return undefined;

    const boundary = votes.map(median);
    if (boundary.some((b) => b === undefined)) return undefined; // a grid line had no data

    const widths: number[] = [];
    for (let c = 0; c < colCount; c++) {
        const w = boundary[c + 1]! - boundary[c]!;
        if (w <= 0) return undefined; // non-monotonic boundaries → corrupt data, bail
        widths.push(Math.round(w));
    }

    if (process.env.NODE_ENV !== "production") {
        votes.forEach((v, i) => {
            if (v.length > 1) {
                const spread = Math.max(...v) - Math.min(...v);
                if (spread > JITTER_TOL) {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `[tableColumns] column boundary ${i} disagrees across rows by ` +
                            `${Math.round(spread)}px — proportions are approximate`,
                    );
                }
            }
        });
    }

    return widths;
}
